import { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NativeChessBoard } from '@/components/NativeChessBoard';
import { BotChatPanel } from '@/components/BotChatPanel';
import { PlayerInfoCard } from '@/components/PlayerInfoCard';
import { GameControls } from '@/components/GameControls';
import { GameRewardsModal } from '@/components/ui/GameRewardsModal';
import { AvatarDisplay } from '@/components/AvatarDisplay';
import { createInitialGameState, GameState, validateMove, makeMove, Position, getAllLegalMoves, getGameStatus } from '@/lib/chess-engine';
import { supabase } from '@/integrations/supabase/client';
import { getEngineMove } from '@/lib/unified-engine';
import { calculateGameRewards } from '@/lib/gameStateUtils';
import { useToast } from '@/hooks/use-toast';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useLocalClock } from '@/hooks/useLocalClock';
import { useCurrency } from '@/hooks/useCurrency';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { Clock, Flag, Sparkles } from 'lucide-react';
import { 
  generateBotMessage, 
  shouldBotReact, 
  type ChatMessage, 
  type ChatTone, 
  type BotChatEvent 
} from '@/lib/bot-chat';
import { getBotNameByElo } from '@/lib/bot-constants';
import type { AvatarConfig } from '@/lib/avatar-config';

interface BotMultiplayerBoardProps {
  roomCode: string;
  timeControl: string;
  botElo: number;
  botName?: string;
  botAvatarConfig?: AvatarConfig;
  botPersonality?: string;
  botPlayStyle?: string;
  playerColor: 'white' | 'black';
  isRated: boolean;
  onLeaveRoom: () => void;
}

export function BotMultiplayerBoard({ 
  roomCode, 
  timeControl, 
  botElo,
  botName,
  botAvatarConfig,
  botPersonality,
  botPlayStyle,
  playerColor,
  isRated,
  onLeaveRoom 
}: BotMultiplayerBoardProps) {
  // Get display name from prop or look up from ELO
  const displayBotName = botName || getBotNameByElo(botElo);
  const { profile } = useUserProfile();
  const { awardCurrency } = useCurrency();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  // Race condition prevention - ensure we only award currency once
  const currencyAwardInProgressRef = useRef(false);
  const completionProcessedRef = useRef(false);
  const hasAwardedRef = useRef(false);
  const [gameState, setGameState] = useState<GameState>(createInitialGameState());
  const [currentTurn, setCurrentTurn] = useState<'white' | 'black'>('white');
  const [currentPlayerColor, setCurrentPlayerColor] = useState<'white' | 'black'>(playerColor);
  const [isThinking, setIsThinking] = useState(playerColor === 'black'); // If black, bot moves first
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [validMoves, setValidMoves] = useState<Position[]>([]);
  const [gameHistory, setGameHistory] = useState<GameState[]>([createInitialGameState()]);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [gameSessionId, setGameSessionId] = useState<string | null>(null);
  const [isProcessingRewards, setIsProcessingRewards] = useState(false);
  
  // Store pre-calculated game outcome to avoid stale closures
  const [gameOutcome, setGameOutcome] = useState<{
    isPlayerWinner: boolean;
    isDraw: boolean;
    rewards: { coins: number; gems: number };
    timeIsUp: boolean;
    status: any;
  } | null>(null);
  
  // Game rewards modal state
  const [showRewardsModal, setShowRewardsModal] = useState(false);
  const [gameResult, setGameResult] = useState<{
    isWinner: boolean;
    coinsEarned: number;
    gemsEarned: number;
    eloChange?: number;
    oldElo?: number;
    newElo?: number;
  } | null>(null);
  
  // Bot chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatTone, setChatTone] = useState<ChatTone>('friendly');
  
  const { toast } = useToast();
  
  // Game status and clock
  const status = getGameStatus(gameState);
  const gameStatusStr = status.status === 'ongoing' ? 'ongoing' : status.status;
  
  const { whiteTimeMs, blackTimeMs, isTimeUp } = useLocalClock(
    timeControl, 
    currentTurn, 
    gameStatusStr
  );

  // Bot games are client-side only - no database session needed
  // Removed game session creation to prevent orphaned entries

  // Helper function to add bot chat message
  const addBotMessage = useCallback((event: BotChatEvent, customMessage?: string) => {
    if (!shouldBotReact(event, botElo, chatTone)) return;
    
    const message = customMessage || generateBotMessage(event, botElo, chatTone);
    if (!message) return;
    
    const chatMessage: ChatMessage = {
      id: `bot-${Date.now()}-${Math.random()}`,
      sender: 'bot',
      message,
      timestamp: Date.now()
    };
    
    setChatMessages(prev => [...prev, chatMessage]);
  }, [botElo, chatTone]);

  // Refs for stable function references (prevent effect re-runs)
  const awardCurrencyRef = useRef(awardCurrency);
  const addBotMessageRef = useRef(addBotMessage);
  const toastRef = useRef(toast);

  // Helper function to handle player chat messages
  const handlePlayerMessage = useCallback((message: string) => {
    const playerMessage: ChatMessage = {
      id: `player-${Date.now()}`,
      sender: 'player',
      message,
      timestamp: Date.now()
    };
    setChatMessages(prev => [...prev, playerMessage]);
    
    // Bot response after a short delay
    setTimeout(() => {
      addBotMessage('player_message');
    }, 1000 + Math.random() * 2000);
  }, [addBotMessage]);

  const makePlayerMove = useCallback(async (from: Position, to: Position) => {
    if (currentTurn !== playerColor || isThinking) return false;

    // Validate move
    const isValidMove = validateMove(gameState, from, to);
    if (!isValidMove) {
      toast({
        title: "Invalid Move",
        description: "That move is not allowed",
        variant: "destructive"
      });
      return false;
    }

    // Make the move
    const newGameState = makeMove(gameState, from, to);
    setGameState(newGameState);
    setGameHistory(prev => [...prev, newGameState]);
    const opponentColor = playerColor === 'white' ? 'black' : 'white';
    setCurrentTurn(opponentColor);
    setIsThinking(true);
    setSelectedSquare(null);
    setValidMoves([]);

    // Check for immediate game end after player move
    const newStatus = getGameStatus(newGameState);
    const isTerminal = newStatus.status !== 'ongoing' && newStatus.status !== 'check';
    
    if (isTerminal) {
      console.log('🏁 Game ended after player move:', newStatus.status);
      const isPlayerWinner = newStatus.status === 'checkmate' && newStatus.winner === playerColor;
      const isDraw = newStatus.status === 'stalemate' || newStatus.status === 'draw';
      const rewards = calculateGameRewards(isPlayerWinner, 'bot', isDraw);
      
      setGameCompleted(true);
      await processRewardsNow({
        isPlayerWinner,
        isDraw,
        rewards,
        timeIsUp: false,
        status: newStatus
      });
    }

    // Update game session in database
    if (gameSessionId) {
      try {
        await supabase
          .from('game_sessions')
          .update({
            move_count: gameHistory.length + 1
          })
          .eq('id', gameSessionId);
      } catch (error) {
        console.error('Failed to update game session:', error);
      }
    }

    // Game completion will be detected by the continuous monitoring useEffect

    // Add bot chat reactions for significant events only
    const wasCapture = gameState.board[to[0]][to[1]] !== null;
    const wasCheck = newStatus.status === 'check';
    
    setTimeout(() => {
      if (wasCapture) {
        addBotMessage('capture');
      } else if (wasCheck) {
        addBotMessage('check');
      }
      // Remove routine player_move messages - only react to significant events
    }, 500);

    // Get bot response after a short delay
    setTimeout(async () => {
      try {
        // Use native engine for weak bots (≤ 1000 ELO), Stockfish for stronger ones
        const engineConfig = botElo <= 1000 
          ? { preferredEngine: 'native' as const, fallbackEnabled: true }
          : { preferredEngine: 'stockfish' as const, fallbackEnabled: true };
        
        const botMove = await getEngineMove(newGameState, botElo, engineConfig, newGameState.fullmoveNumber);
        
        if (botMove && botMove.move) {
          const botGameState = makeMove(newGameState, botMove.move.from, botMove.move.to);
          setGameState(botGameState);
          setGameHistory(prev => [...prev, botGameState]);
          setCurrentTurn(playerColor);
          
          // Check for immediate game end after bot move
          const botStatus = getGameStatus(botGameState);
          const isBotTerminal = botStatus.status !== 'ongoing' && botStatus.status !== 'check';
          
          if (isBotTerminal) {
            console.log('🏁 Game ended after bot move:', botStatus.status);
            const isPlayerWinner = botStatus.status === 'checkmate' && botStatus.winner === playerColor;
            const isDraw = botStatus.status === 'stalemate' || botStatus.status === 'draw';
            const rewards = calculateGameRewards(isPlayerWinner, 'bot', isDraw);
            
            setGameCompleted(true);
            await processRewardsNow({
              isPlayerWinner,
              isDraw,
              rewards,
              timeIsUp: false,
              status: botStatus
            });
          }
          
          // Update game session after bot move
          if (gameSessionId) {
            try {
              await supabase
                .from('game_sessions')
                .update({
                  move_count: gameHistory.length + 2
                })
                .eq('id', gameSessionId);
            } catch (error) {
              console.error('Failed to update game session after bot move:', error);
            }
          }
          
          // Bot move message - only for interesting moves
          const botWasCapture = newGameState.board[botMove.move.to[0]][botMove.move.to[1]] !== null;
          const botGaveCheck = botStatus.status === 'check';
          
          if (botWasCapture || botGaveCheck || Math.random() < 0.1) {
            setTimeout(() => {
              addBotMessage('bot_move');
            }, 500);
          }
        } else {
          console.error('Bot failed to make a move');
        setCurrentTurn(playerColor);
        toast({
          title: "Bot Error",
            description: "The bot encountered an error. Try making another move.",
          variant: "destructive"
        });
      }
      } catch (error) {
        console.error('Bot move error details:', { botElo, gameState, error });
        toast({
          title: "Bot Error", 
          description: `The bot encountered an error (${error instanceof Error ? error.message : 'unknown'}). Try making another move.`,
          variant: "destructive"
        });
        setCurrentTurn('white');
      } finally {
        setIsThinking(false);
      }
    }, 500 + Math.random() * 1500); // Random delay between 0.5-2s for realism

    return true;
  }, [gameState, currentTurn, isThinking, botElo, toast, addBotMessage]);

  const handleSquareClick = useCallback(async (position: Position) => {
    console.log('Bot board square clicked:', position, 'Current turn:', currentTurn, 'Is thinking:', isThinking);
    
    const [row, col] = position;
    const piece = gameState.board[row][col];
    console.log('Piece at clicked position:', piece);

    // Priority 1: If clicking on player's piece, always select it (even if another piece is selected)
    if (piece && piece.color === playerColor) {
      console.log('Selecting player piece:', piece);
      setSelectedSquare(position);
      const moves = getAllLegalMoves(gameState).filter(move => 
        move.from[0] === row && move.from[1] === col
      ).map(move => move.to);
      console.log('Valid moves:', moves);
      setValidMoves(moves);
      return;
    }

    // Only proceed with move logic if it's player's turn and bot isn't thinking
    if (currentTurn !== playerColor || isThinking) {
      console.log('Cannot make moves: not player turn or bot is thinking');
      if (currentTurn !== playerColor) {
        toast({
          title: "Not Your Turn",
          description: "Wait for the bot to make its move",
          variant: "destructive"
        });
      }
      return;
    }

    if (selectedSquare) {
      const [selectedRow, selectedCol] = selectedSquare;
      const selectedPiece = gameState.board[selectedRow][selectedCol];

      // If clicking on same square, deselect
      if (selectedRow === row && selectedCol === col) {
        console.log('Deselecting same square');
        setSelectedSquare(null);
        setValidMoves([]);
        return;
      }

      // If valid move, execute it
      if (validMoves.some(([r, c]) => r === row && c === col)) {
        console.log('Executing move from', selectedSquare, 'to', position);
        const success = await makePlayerMove(selectedSquare, position);
        return;
      }

      // If clicking on invalid square, keep current selection (don't deselect)
      console.log('Clicked on invalid square, keeping current selection');
    } else {
      console.log('No piece selected and clicked on empty square');
    }
  }, [gameState, selectedSquare, validMoves, currentTurn, isThinking, makePlayerMove, toast]);

  const handleTakeback = () => {
    if (gameHistory.length > 1) {
      const previousState = gameHistory[gameHistory.length - 2];
      setGameState(previousState);
      setGameHistory(prev => prev.slice(0, -1));
      setSelectedSquare(null);
      setValidMoves([]);
    }
  };

  const handleResign = useCallback(async () => {
    console.log('🏳️ Player resigned');
    
    // Mark game as completed with loss
    setGameCompleted(true);
    
    // Calculate loss penalty
    const rewards = calculateGameRewards(false, 'bot', false); // isWinner=false, isDraw=false
    
    console.log('💰 Resignation penalty:', rewards);
    
    try {
      // Pre-flight check: Ensure user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        console.error('❌ No active session for currency penalty');
        toast({
          title: "Session Expired",
          description: "Please log in again.",
          variant: "destructive"
        });
        onLeaveRoom(); // Still leave the room
        return;
      }
      
      console.log('💳 Applying resignation penalty...', {
        userId: session.user.id,
        rewards
      });
      
      // Mark game session as completed (resignation)
      if (gameSessionId) {
        try {
          await supabase
            .from('game_sessions')
            .update({
              game_status: 'resignation',
              winner: 'black' // Bot wins on resignation
            })
            .eq('id', gameSessionId);
          
          console.log('✅ Game session marked as resigned:', gameSessionId);
        } catch (error) {
          console.error('❌ Failed to update game session on resignation:', error);
        }
      }
      
      // Award (negative) currency for resignation
      const result = await awardCurrency(
        rewards.coins, // This will be -10
        rewards.gems,  // This will be 0
        'bot_game_completion',
        {
          game_type: 'bot',
          bot_elo: botElo,
          game_session_id: gameSessionId, // ✅ Include game_session_id
          result: 'resignation',
          room_code: roomCode
        }
      );
      
      console.log('✅ Resignation penalty applied:', result);
      
      // Trigger manual refresh
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('currency-updated'));
      }, 500);
      
      // Set game result for modal
      setGameResult({
        isWinner: false,
        coinsEarned: rewards.coins, // Will be -10
        gemsEarned: rewards.gems
      });
      
      // Show modal
      setTimeout(() => {
        setShowRewardsModal(true);
      }, 500);
      
      // Bot victory message
      setTimeout(() => {
        addBotMessage('victory');
      }, 1000);
      
      // Leave room after showing results
      setTimeout(() => {
        onLeaveRoom();
      }, 3000);
      
    } catch (error: any) {
      console.error('❌ Error applying resignation penalty:', error);
      
      if (error?.message === 'USER_NOT_AUTHENTICATED') {
        toast({
          title: "Please Log In",
          description: "Your game is complete! Log in to see results.",
          variant: "destructive",
          duration: 5000
        });
      } else {
        toast({
          title: "Penalty Error",
          description: "Failed to apply penalty. Please contact support.",
          variant: "destructive"
        });
      }
      
      // Still leave the room even if currency fails
      setTimeout(() => {
        onLeaveRoom();
      }, 2000);
    }
  }, [awardCurrency, botElo, roomCode, onLeaveRoom, addBotMessage, toast]);

  const getGameStatusMessage = () => {
    if (isTimeUp) {
      const winner = whiteTimeMs <= 0 ? 'Black' : 'White';
      return `Time's up! ${winner} wins!`;
    }
    
    if (status.status !== 'ongoing') {
      return status.status === 'checkmate' ? `Checkmate! ${status.winner === 'white' ? 'White' : 'Black'} wins` :
             status.status === 'stalemate' ? 'Stalemate - Draw!' :
             status.status === 'draw' ? `Draw - ${status.reason}` :
             status.status === 'check' ? 'Check!' : '';
    }
    
    return `${gameState.sideToMove === 'white' ? 'White' : 'Black'} to move`;
  };

  // Update refs when functions change to ensure we always use latest versions
  useEffect(() => {
    awardCurrencyRef.current = awardCurrency;
    addBotMessageRef.current = addBotMessage;
    toastRef.current = toast;
  }, [awardCurrency, addBotMessage, toast]);

  // Helper function to immediately process rewards (prevent navigation loss)
  const processRewardsNow = useCallback(async (outcome: {
    isPlayerWinner: boolean;
    isDraw: boolean;
    rewards: { coins: number; gems: number };
    timeIsUp: boolean;
    status: any;
  }) => {
    // Guard: Already awarded
    if (hasAwardedRef.current) {
      console.log('⏭️ Rewards already processed, skipping');
      return;
    }
    
    // Guard: Award in progress
    if (currencyAwardInProgressRef.current) {
      console.log('⏳ Award already in progress, skipping');
      return;
    }
    
    console.log('💰 Processing rewards NOW:', outcome);
    
    // Set flags immediately (synchronous) and show blocking UI
    hasAwardedRef.current = true;
    currencyAwardInProgressRef.current = true;
    setIsProcessingRewards(true);
    
    let eloResult: { elo_change?: number; old_elo?: number; new_elo?: number } = {};
    
    try {
      if (!user?.id) {
        console.error('❌ No user available');
        currencyAwardInProgressRef.current = false;
        setIsProcessingRewards(false);
        toast({
          title: "Authentication Error",
          description: "Please refresh the page and try again.",
          variant: "destructive"
        });
        return;
      }
      
      // Mark game session as completed
      if (gameSessionId) {
        try {
          const winnerColor = outcome.isPlayerWinner ? playerColor : (playerColor === 'white' ? 'black' : 'white');
          await supabase
            .from('game_sessions')
            .update({
              game_status: outcome.isDraw ? 'draw' : 'checkmate',
              winner: outcome.isDraw ? null : winnerColor
            })
            .eq('id', gameSessionId);
          
          console.log('✅ Game session marked as completed:', gameSessionId);
        } catch (error) {
          console.error('❌ Failed to update game session:', error);
        }
      }
      
      // Update Elo if rated game
      if (isRated) {
        console.log('📊 Updating Elo rating...', {
          userId: user.id,
          botElo,
          result: outcome.isPlayerWinner ? 'win' : (outcome.isDraw ? 'draw' : 'loss'),
          isRated
        });
        
        try {
          const { data: eloData, error: eloError } = await supabase.rpc('update_player_elo_for_bot_game', {
            _player_id: user.id,
            _bot_elo: botElo,
            _game_result: outcome.isPlayerWinner ? 'win' : (outcome.isDraw ? 'draw' : 'loss'),
            _is_rated: true,
            _game_session_id: gameSessionId
          });
          
          if (eloError) {
            console.error('❌ Elo update error:', eloError);
          } else if (eloData) {
            console.log('✅ Elo updated:', eloData);
            const eloResponse = eloData as { elo_change: number; old_elo: number; new_elo: number };
            eloResult = {
              elo_change: eloResponse.elo_change,
              old_elo: eloResponse.old_elo,
              new_elo: eloResponse.new_elo
            };
            
            // Show Elo change toast
            if (eloResponse.elo_change !== 0) {
              toast({
                title: eloResponse.elo_change > 0 ? "Rating Increased! 📈" : "Rating Decreased 📉",
                description: `${eloResponse.old_elo} → ${eloResponse.new_elo} (${eloResponse.elo_change > 0 ? '+' : ''}${eloResponse.elo_change})`,
                duration: 5000
              });
            }
          }
        } catch (error) {
          console.error('❌ Elo update failed:', error);
        }
      }
      
      console.log('💳 Awarding currency...', {
        userId: user.id,
        rewards: outcome.rewards,
        timestamp: new Date().toISOString()
      });
      
      // Create timeout promise (10 second safety)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Award timeout after 10 seconds')), 10000)
      );
      
      // Award currency with timeout protection
      const awardPromise = awardCurrency(
        outcome.rewards.coins,
        outcome.rewards.gems,
        'bot_game_completion',
        {
          game_type: 'bot',
          bot_elo: botElo,
          game_session_id: gameSessionId,
          result: outcome.isPlayerWinner ? 'win' : (outcome.isDraw ? 'draw' : 'loss'),
          room_code: roomCode,
          is_rated: isRated
        }
      );
      
      const result = await Promise.race([awardPromise, timeoutPromise]);
      
      console.log('✅ Currency awarded:', result);
      
      // Trigger UI updates
      window.dispatchEvent(new CustomEvent('currency-updated'));
      
      setGameResult({
        isWinner: outcome.isPlayerWinner,
        coinsEarned: outcome.rewards.coins,
        gemsEarned: outcome.rewards.gems,
        eloChange: eloResult.elo_change,
        oldElo: eloResult.old_elo,
        newElo: eloResult.new_elo
      });
      
      setTimeout(() => setShowRewardsModal(true), 500);
      
      setTimeout(() => {
        if (outcome.isPlayerWinner) {
          addBotMessage('defeat');
        } else {
          addBotMessage('victory');
        }
      }, 1000);
      
    } catch (error: any) {
      console.error('❌ Error awarding currency:', error);
      
      if (error?.message?.includes('timeout')) {
        toast({
          title: "Network Timeout",
          description: "Currency award is taking longer than expected. It may still process.",
          variant: "destructive"
        });
      } else if (error?.message === 'USER_NOT_AUTHENTICATED') {
        toast({
          title: "Please Log In",
          description: "Your game is complete! Log in to receive your rewards.",
          variant: "destructive",
          duration: 5000
        });
      } else {
        toast({
          title: "Reward Error",
          description: "Failed to award currency. Please contact support.",
          variant: "destructive"
        });
      }
    } finally {
      currencyAwardInProgressRef.current = false;
      setIsProcessingRewards(false);
    }
  }, [user, gameSessionId, botElo, roomCode, awardCurrency, toast, addBotMessage, isRated, playerColor]);

  // Bot greeting effect
  useEffect(() => {
    if (chatMessages.length === 0) {
      setTimeout(() => {
        addBotMessage('greeting');
      }, 1000);
    }
  }, [addBotMessage, chatMessages.length]);

  // Bot makes first move when player is black
  useEffect(() => {
    if (playerColor === 'black' && gameHistory.length === 1 && !gameCompleted) {
      console.log('🤖 Bot moving first (player is black)...');
      setIsThinking(true);
      
      setTimeout(async () => {
        try {
          const engineConfig = botElo <= 1000 
            ? { preferredEngine: 'native' as const, fallbackEnabled: true }
            : { preferredEngine: 'stockfish' as const, fallbackEnabled: true };
          
          const botMove = await getEngineMove(gameState, botElo, engineConfig, gameState.fullmoveNumber);
          
          if (botMove && botMove.move) {
            const botGameState = makeMove(gameState, botMove.move.from, botMove.move.to);
            setGameState(botGameState);
            setGameHistory(prev => [...prev, botGameState]);
            setCurrentTurn('black'); // Now it's black's (player's) turn
            console.log('✅ Bot made first move');
          }
        } catch (error) {
          console.error('Bot first move error:', error);
          toast({
            title: "Bot Error",
            description: "Bot failed to make first move. Please try again.",
            variant: "destructive"
          });
        } finally {
          setIsThinking(false);
        }
      }, 800);
    }
  }, [playerColor, gameHistory.length, gameCompleted, gameState, botElo, toast]);

  // STEP 1: Detection-only effect - monitors game state, calculates results, and stores them
  // This effect uses FRESH values from dependencies to calculate game outcome
  useEffect(() => {
    if (completionProcessedRef.current) {
      return;
    }
    
    const status = getGameStatus(gameState);
    const isGameOver = status.status !== 'ongoing' && status.status !== 'check';
    const timeIsUp = whiteTimeMs <= 0 || blackTimeMs <= 0;
    
    console.log('🎮 Game status check:', {
      status: status.status,
      isGameOver,
      timeIsUp,
      gameCompleted
    });
    
    if (isGameOver || timeIsUp) {
      console.log('🏁 Game is over! Calculating results with FRESH values...');
      
      // Calculate winner with fresh game state values - correctly based on playerColor
      const opponentColor = playerColor === 'white' ? 'black' : 'white';
      let isPlayerWinner = false;
      if (timeIsUp) {
        // Player wins if opponent's time ran out
        const opponentTimeMs = playerColor === 'white' ? blackTimeMs : whiteTimeMs;
        isPlayerWinner = opponentTimeMs <= 0;
      } else if (status.status === 'checkmate') {
        isPlayerWinner = status.winner === playerColor;
      }
      
      const isDraw = !timeIsUp && (status.status === 'stalemate' || status.status === 'draw');
      const rewards = calculateGameRewards(isPlayerWinner, 'bot', isDraw);
      
      console.log('✅ Game outcome calculated:', {
        isPlayerWinner,
        isDraw,
        rewards,
        timeIsUp,
        status: status.status
      });
      
      // Store calculated results in state
      setGameOutcome({
        isPlayerWinner,
        isDraw,
        rewards,
        timeIsUp,
        status
      });
      
      completionProcessedRef.current = true;
      setGameCompleted(true);
      
      // Debounce reward processing to prevent race conditions
      setTimeout(() => {
        if (!hasAwardedRef.current && !currencyAwardInProgressRef.current) {
          console.log('⏱️ Debounced reward processing triggered');
          processRewardsNow({
            isPlayerWinner,
            isDraw,
            rewards,
            timeIsUp,
            status
          });
        }
      }, 200); // 200ms debounce
    }
  }, [gameState, whiteTimeMs, blackTimeMs]);

  // Reset completion flags when a new game starts or room changes
  useEffect(() => {
    console.log('🔄 Resetting completion flags for new game/room');
    completionProcessedRef.current = false;
    currencyAwardInProgressRef.current = false;
    hasAwardedRef.current = false;
    setGameSessionId(null); // Reset session ID for new game
  }, [roomCode]);

  // Safety net: Process rewards on unmount if game ended but not awarded
  useEffect(() => {
    return () => {
      if (gameOutcome && !hasAwardedRef.current) {
        console.log('⚠️ Unmounting with unawarded game, processing now');
        processRewardsNow(gameOutcome);
      }
    };
  }, [gameOutcome, processRewardsNow]);


  // Helper function to format time
  const formatTime = (ms: number) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className="h-screen flex bg-background">
        {/* Mobile Layout */}
        {isMobile ? (
          <div className="flex flex-col w-full h-full">
            {/* Top Player Info - Bot */}
            <div className="flex items-center justify-between px-3 py-2 bg-card border-b border-border">
              <div className="flex items-center gap-2">
                <AvatarDisplay
                  username={displayBotName}
                  avatarConfig={botAvatarConfig}
                  size="sm"
                />
                <div className="flex flex-col">
                  <span className="font-semibold text-sm">{displayBotName}</span>
                  <Badge variant="outline" className="text-xs w-fit">{botElo} ELO</Badge>
                </div>
              </div>
              <div className="flex items-center gap-1.5 font-mono font-bold text-base">
                <Clock className="w-4 h-4" />
                <span>{formatTime(playerColor === 'white' ? blackTimeMs : whiteTimeMs)}</span>
              </div>
            </div>

            {/* Chess Board */}
            <div className="flex-1 flex items-center justify-center p-1 min-h-0 overflow-hidden">
              <div className="w-full h-full max-w-[calc(100dvh-220px)] max-h-full aspect-square mx-auto">
                <NativeChessBoard
                  gameState={gameState}
                  onSquareClick={handleSquareClick}
                  highlightedSquares={validMoves}
                  selectedSquare={selectedSquare}
                  flipped={playerColor === 'black'}
                  fillParent={true}
                  compactPadding={true}
                />
              </div>
            </div>

            {/* Bottom Player Info - User */}
            <div className="flex items-center justify-between px-3 py-2 bg-card border-t border-border">
              <div className="flex items-center gap-2">
                <AvatarDisplay
                  username={profile?.display_name || profile?.username || "You"}
                  avatarUrl={profile?.avatar_url}
                  avatarConfig={profile?.avatar_config}
                  size="sm"
                />
                <div className="flex flex-col">
                  <span className="font-semibold text-sm">{profile?.display_name || profile?.username || "You"}</span>
                  <Badge variant="outline" className="text-xs w-fit">{profile?.elo_rating || 1200} ELO</Badge>
                </div>
              </div>
              <div className="flex items-center gap-1.5 font-mono font-bold text-base">
                <Clock className="w-4 h-4" />
                <span>{formatTime(playerColor === 'white' ? whiteTimeMs : blackTimeMs)}</span>
              </div>
            </div>

            {/* Action Bar */}
            <div className="flex items-center justify-center gap-2 px-3 py-1.5 bg-card border-t border-border">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleResign}
                disabled={gameCompleted}
                className="flex-1"
              >
                <Flag className="w-4 h-4 mr-1" />
                Resign
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleTakeback}
                disabled={gameHistory.length <= 1 || gameCompleted}
                className="flex-1"
              >
                Takeback
              </Button>
            </div>
          </div>
        ) : (
          /* Desktop Layout */
          <>
            {/* Left Area - Bot Info & Game Status */}
            <div className="w-72 border-r border-border bg-card overflow-y-auto flex-col flex">
              <div className="p-4 space-y-4">
                {/* Bot Player Info */}
                <div className="space-y-3">
                  <PlayerInfoCard
                    name={displayBotName}
                    isBot={true}
                    elo={botElo}
                    color={playerColor === 'white' ? 'black' : 'white'}
                    isActivePlayer={currentTurn !== playerColor && !isTimeUp}
                    timeMs={playerColor === 'white' ? blackTimeMs : whiteTimeMs}
                    isCurrentUser={false}
                    isConnected={true}
                    compact={true}
                    avatarConfig={botAvatarConfig}
                  />
                  
                  {/* Bot Personality Card */}
                  {botPersonality && botPlayStyle && (
                    <Card className="p-3 bg-muted/30 border-border/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">About {displayBotName}</span>
                      </div>
                      <p className="text-sm text-muted-foreground italic mb-2">"{botPersonality}"</p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">Play style:</span> {botPlayStyle}
                      </p>
                    </Card>
                  )}
                  
                  {/* Game Status */}
                  <Card className="p-4 text-center border border-border/50">
                    <div className="mb-3 inline-flex items-center px-3 py-1 bg-primary/10 rounded-full text-primary text-xs font-medium">
                      🤖 Bot Match
                    </div>
                    <div className={`text-sm font-semibold ${
                      isTimeUp || (status.status !== 'ongoing' && status.status !== 'check') 
                        ? 'text-destructive' 
                        : currentTurn === 'white' 
                          ? 'text-primary' 
                          : 'text-muted-foreground'
                    }`}>
                      {getGameStatusMessage()}
                    </div>
                  </Card>
                </div>

                {/* Game Controls */}
                <Card className="p-3 border border-border/50">
                  <h3 className="text-sm font-semibold mb-3 text-card-foreground">Game Controls</h3>
                  <GameControls
                    onTakeback={handleTakeback}
                    onResign={handleResign}
                    canTakeback={gameHistory.length > 1}
                  />
                </Card>
              </div>
            </div>

            {/* Center - Chess Board */}
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="max-w-2xl w-full aspect-square">
                <NativeChessBoard
                  gameState={gameState}
                  onSquareClick={handleSquareClick}
                  highlightedSquares={validMoves}
                  selectedSquare={selectedSquare}
                  flipped={playerColor === 'black'}
                />
              </div>
            </div>

            {/* Right Area - User Info & Chat */}
            <div className="w-80 border-l border-border bg-card flex-col h-full flex">
              <div className="p-4 space-y-4 flex flex-col h-full min-h-0">
                {/* User Player Info */}
                <div className="flex-shrink-0">
                  <PlayerInfoCard
                    name={profile?.display_name || "You"}
                    isBot={false}
                    elo={profile?.elo_rating}
                    color={playerColor}
                    isActivePlayer={currentTurn === playerColor && !isTimeUp}
                    timeMs={playerColor === 'white' ? whiteTimeMs : blackTimeMs}
                    isCurrentUser={true}
                    isConnected={true}
                    compact={true}
                  />
                </div>
                
                {/* Bot Chat Panel */}
                <div className="flex-1 min-h-0">
                  <BotChatPanel
                    messages={chatMessages}
                    onSend={handlePlayerMessage}
                    tone={chatTone}
                    onToneChange={setChatTone}
                    botName={displayBotName}
                    isVisible={true}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Game Rewards Modal */}
      {gameResult && (
        <GameRewardsModal
          open={showRewardsModal}
          onOpenChange={setShowRewardsModal}
          isWinner={gameResult.isWinner}
          coinsEarned={gameResult.coinsEarned}
          gemsEarned={gameResult.gemsEarned}
          gameType="bot"
          opponentName={displayBotName}
          eloChange={gameResult.eloChange}
          oldElo={gameResult.oldElo}
          newElo={gameResult.newElo}
          isRated={isRated}
          showRematchButton={true}
          onRematch={(swapColors) => {
            // Reset game state for rematch
            setGameState(createInitialGameState());
            setGameHistory([createInitialGameState()]);
            setCurrentTurn('white');
            setSelectedSquare(null);
            setValidMoves([]);
            setGameCompleted(false);
            setGameResult(null);
            setGameOutcome(null);
            completionProcessedRef.current = false;
            currencyAwardInProgressRef.current = false;
            hasAwardedRef.current = false;
            
            // Swap colors if requested
            if (swapColors) {
              setCurrentPlayerColor(prev => prev === 'white' ? 'black' : 'white');
              setIsThinking(currentPlayerColor === 'white'); // If swapping to black, bot moves first
            } else {
              setIsThinking(currentPlayerColor === 'black');
            }
            
            toast({
              title: swapColors ? "Colors Swapped!" : "Rematch Started!",
              description: swapColors 
                ? `You're now playing as ${currentPlayerColor === 'white' ? 'black' : 'white'}`
                : "Good luck in your next game!",
            });
          }}
        />
      )}
      
      {/* Blocking Rewards Processing Overlay */}
      {isProcessingRewards && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <Card className="p-6 space-y-4 max-w-md mx-4">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-lg font-semibold">Processing your rewards...</p>
            </div>
            <p className="text-sm text-muted-foreground">Please wait, do not close this window.</p>
          </Card>
        </div>
      )}
    </>
  );
}